import React from "react";
import { sortedEntries } from "../../utils";
import { formatGradeRange } from "../../gradeUtils";
import { getGymDisciplines, getDifficultyForDiscipline } from "../../utils/disciplineUtils";
import { useTranslation, tDiffLabel } from "../../i18n";
import { useAppTheme } from "../../contexts/ThemeContext";

/**
 * GradeRangeSummary — renders a gym's supported difficulty levels as colored
 * pills (label + grade range), grouped per discipline for mixed gyms.
 *
 * Shared between the GymSelector card "more info" panel and the GymInfoModal so
 * both surfaces show the same grade-range section. Returns null when the gym
 * has no graded difficulties.
 *
 * `gym` must be the FULL gym doc (with `settings` + `disciplines`), not the
 * lightweight list item used in the selector grid. Pass `style` to control the
 * outer wrapper spacing (the card and the modal use different top margins).
 */
export function GradeRangeSummary({ gym, style }) {
  const { th } = useAppTheme();
  const { t } = useTranslation();
  if (!gym) return null;
  const disciplines = getGymDisciplines(gym);
  const sections = disciplines
    .map(disc => ({
      disc,
      diff: getDifficultyForDiscipline(gym.settings, disc, disciplines),
    }))
    .filter(s => s.diff && Object.values(s.diff).some(d => d.gradeRange));
  if (sections.length === 0) return null;
  const showDisciplineHeader = sections.length > 1;
  return (
    <div style={style}>
      <div style={{ fontSize: 12, fontWeight: 700, color: th.textMuted, marginBottom: 4 }}>{t('grade.gradeRange')}</div>
      {sections.map(({ disc, diff }) => (
        <div key={disc} style={{ marginBottom: showDisciplineHeader ? 6 : 0 }}>
          {showDisciplineHeader && (
            <div style={{ fontSize: 12, fontWeight: 700, color: th.textMuted, marginBottom: 3 }}>
              {t(`discipline.${disc}`)}
            </div>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 4 }}>
            {sortedEntries(diff).filter(([, d]) => d.gradeRange).map(([key, d]) => (
              <span key={key} style={{
                padding: "2px 8px", borderRadius: 8, fontSize: 12, fontWeight: 600,
                background: d.color, color: d.tc || "#333",
                border: "1px solid " + th.border,
              }}>
                {tDiffLabel(t, key, d.label)}: {formatGradeRange(d.gradeRange, "font")}
              </span>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
